import { useTrial } from '@/hooks/use-trial';
import { useLicenseStore } from '@/stores/license-store';

interface TrialBadgeProps {
  /** Use compact style for tight spaces (e.g. sidebar nav) */
  compact?: boolean;
}

/**
 * Contextual badge showing trial/subscription status.
 * Renders next to the app logo in the header and sidebar nav.
 *
 * States:
 * - Trial active: amber "Trial (Xd)"
 * - Paid: teal "Paid"
 * - Expired: red "Expired"
 * - Excluded app / loading: nothing
 */
export function TrialBadge({ compact = false }: TrialBadgeProps) {
  const { badgeStatus, trialLoaded, licensingApplies } = useTrial();
  const licenseLoaded = useLicenseStore((s) => s.licenseLoaded);

  // Wait for both trial AND license data so paid users don't briefly
  // see a "Trial" / "Expired" badge before their subscription resolves.
  if (!licensingApplies || !trialLoaded || !licenseLoaded) return null;

  if (badgeStatus === 'excluded') return null;

  if (badgeStatus === 'paid') {
    return (
      <span className="rounded-full bg-teal-500/15 px-2 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-teal-600 dark:text-teal-400">
        Paid
      </span>
    );
  }

  if (badgeStatus === 'trial') {
    return (
      <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
        Trial
      </span>
    );
  }

  // expired — slow pulse to draw attention
  return (
    <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-400 animate-pulse">
      Expired
    </span>
  );
}
